import { faCircleInfo, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Collapse, Tooltip } from "antd";
import type React from "react";
import { useState } from "react";
import styled from "styled-components";
import type { Grant } from "../../types/formTypes";
import OrganizationField from "./OrganizationField";
import TextField from "./TextField";

const FieldWrapper = styled.div`
  margin-bottom: 24px;
  text-align: left;
`;

const LabelRow = styled.label`
  display: inline-flex !important;
  flex-direction: row !important;
  align-items: center;
  gap: 4px;
  margin-bottom: 6px;
  font-weight: 700;
  font-size: 16px;
  cursor: text;
  text-align: left;
`;

const RequiredMark = styled.span`
  color: #d32f2f;
`;

const FundingList = styled.div`
  margin-top: 6px;
`;

const StyledCollapse = styled(Collapse)`
  .ant-collapse-item {
    margin-bottom: 8px;
  }
  .ant-collapse-header {
    align-items: center !important;
  }
  .ant-collapse-header button {
    width: auto !important;
    min-width: unset !important;
    flex: 0 0 auto !important;
    background: none !important;
    border: none !important;
    padding: 4px 8px !important;
  }
  .ant-collapse-content-box {
    padding: 12px 16px !important;
    width: 100%;
    box-sizing: border-box;
  }
`;

const GrantFieldsWrapper = styled.div`
  width: 100%;
  box-sizing: border-box;

  input,
  .ant-select {
    width: 100% !important;
    max-width: 100% !important;
    box-sizing: border-box !important;
  }
`;

const NestedSection = styled.div`
  margin-top: 20px;
  padding: 16px;
  background: rgba(0, 0, 0, 0.02);
  border-radius: 8px;
  border-left: 3px solid var(--color-primary-desat);
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 12px;
`;

const RemoveButton = styled.button`
  flex: 0 0 auto !important;
  background: none !important;
  border: none !important;
  cursor: pointer;
  padding: 4px 8px !important;
  color: var(--color-primary-desat) !important;
  font-size: 0.85rem;
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  width: auto !important;
  min-width: auto !important;
  margin-left: auto;

  &:hover {
    color: #d32f2f !important;
  }
`;

const AddButton = styled.button`
  flex: 0 0 auto !important;
  margin-top: 12px;
  padding: 10px 40px !important;
  font-size: 0.9rem;
  border-radius: 6px;
  border: 1px dashed var(--color-primary-desat) !important;
  background: transparent !important;
  color: var(--color-primary) !important;
  cursor: pointer;

  &:hover {
    border-color: var(--color-primary) !important;
    background: rgba(0, 0, 0, 0.04) !important;
  }
`;

export interface FundingFieldProps {
  label: string;
  name: string;
  value: Grant[];
  onChange: (value: Grant[]) => void;
  required?: boolean;
  tooltip?: string;
  /** Label for each item in the list, e.g. "Grant" */
  itemLabel?: string;
  /** Label for the add button, e.g. "+ Add Funding" */
  addButtonLabel?: string;
}

const emptyGrant: Grant = {
  projectName: "",
  grantNo: "",
  funder: [],
};

const FundingField: React.FC<FundingFieldProps> = ({
  label,
  name,
  value,
  onChange,
  required = false,
  tooltip,
  itemLabel = "Grant",
  addButtonLabel = "+ Add Funding",
}) => {
  const initialItems = value.length > 0 ? value : required ? [emptyGrant] : [];
  const [activeKeys, setActiveKeys] = useState<string[]>(() =>
    initialItems.map((_, i) => String(i)),
  );

  const grants = value.length > 0 ? value : required ? [emptyGrant] : [];

  const updateGrant = (index: number, updates: Partial<Grant>) => {
    const next = [...grants];
    next[index] = { ...next[index], ...updates };
    onChange(next);
  };

  const addGrant = () => {
    const next = [...grants, { ...emptyGrant }];
    onChange(next);
    setActiveKeys((prev) => [...prev, String(next.length - 1)]);
  };

  const removeGrant = (index: number) => {
    if (grants.length <= 1 && required) return;
    const next = grants.filter((_, i) => i !== index);
    onChange(next);
    setActiveKeys((prev) =>
      prev
        .filter((k) => k !== String(index))
        .map((k) =>
          parseInt(k, 10) > index ? String(parseInt(k, 10) - 1) : k,
        ),
    );
  };

  const items = grants.map((grant, index) => ({
    key: String(index),
    label: (
      <PanelHeader>
        <span>{itemLabel}</span>
        {(grants.length > 1 || !required) && grants.length >= 1 && (
          <RemoveButton
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              removeGrant(index);
            }}
            aria-label={`Remove ${itemLabel.toLowerCase()}`}
          >
            <FontAwesomeIcon icon={faTrash} />
          </RemoveButton>
        )}
      </PanelHeader>
    ),
    children: (
      <GrantFieldsWrapper>
        <TextField
          label="Project Name"
          name={`${name}-${index}-projectName`}
          value={grant.projectName ?? ""}
          onChange={(v) => updateGrant(index, { projectName: v })}
          required
          tooltip="Name of the project during which the software was developed."
        />
        <TextField
          label="Identifier"
          name={`${name}-${index}-grantNo`}
          value={grant.grantNo ?? ""}
          onChange={(v) => updateGrant(index, { grantNo: v })}
          tooltip="An identifying grant number, in case of a publicly funded project, this number is assigned by the funding agency."
        />
        <NestedSection>
          <OrganizationField
            label="Funder"
            name={`${name}-${index}-funder`}
            value={Array.isArray(grant.funder) ? grant.funder : []}
            onChange={(v) => updateGrant(index, { funder: v })}
            itemLabel="Organization"
            addButtonLabel="+ Add Organization"
            tooltip="An organization that supports (sponsors) something through some kind of financial contribution."
          />
        </NestedSection>
      </GrantFieldsWrapper>
    ),
  }));

  return (
    <FieldWrapper>
      <LabelRow htmlFor={name}>
        {label}
        {required && <RequiredMark>*</RequiredMark>}
        {tooltip && (
          <Tooltip title={tooltip}>
            <span style={{ display: "inline-flex", cursor: "help" }}>
              <FontAwesomeIcon
                icon={faCircleInfo}
                style={{
                  color: "var(--color-primary-desat)",
                  fontSize: "0.9rem",
                }}
              />
            </span>
          </Tooltip>
        )}
      </LabelRow>
      <FundingList>
        <StyledCollapse
          activeKey={activeKeys}
          onChange={(keys) =>
            setActiveKeys(Array.isArray(keys) ? keys : [keys])
          }
          items={items}
        />
        <AddButton type="button" onClick={addGrant}>
          {addButtonLabel}
        </AddButton>
      </FundingList>
    </FieldWrapper>
  );
};

export default FundingField;
